// src/pages/Notifications/index.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import notificationService from '../../services/notificationService';
import { toast } from 'sonner';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { userProfile } = useAuth();
  
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all'); // all, info, warning, urgent, success, error
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Notifications', path: '/notifications', icon: 'Bell' },
  ];

  // ============================================================================
  // FETCH NOTIFICATIONS
  // ============================================================================

  const fetchNotifications = async () => {
    if (!userProfile?.id) return;

    try {
      setLoading(true);
      const data = await notificationService.getUserNotifications(userProfile.id);
      setNotifications(data);
      setFilteredNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [userProfile?.id]);

  // Real-time subscription
  useEffect(() => {
    if (!userProfile?.id) return;

    const subscription = notificationService.subscribeToNotifications(
      userProfile.id,
      (newNotification) => {
        setNotifications((prev) => [newNotification, ...prev]);
        toast.info(newNotification.title, {
          description: newNotification.message,
        });
      }
    );

    return () => subscription.unsubscribe();
  }, [userProfile?.id]);

  // ============================================================================
  // FILTERING
  // ============================================================================

  useEffect(() => {
    let result = [...notifications];

    // Filter by read status
    if (filter === 'unread') {
      result = result.filter((n) => !n.is_read);
    } else if (filter === 'read') {
      result = result.filter((n) => n.is_read);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      result = result.filter((n) => n.type === typeFilter);
    }

    setFilteredNotifications(result);
  }, [filter, typeFilter, notifications]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      if (!notification.is_read) {
        await notificationService.markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
      }

      // Navigate to link if exists
      if (notification.link) {
        navigate(notification.link);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      toast.success('Marqué comme lu');
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead(userProfile.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (notificationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette notification ?')) return;

    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      toast.success('Notification supprimée');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNotifications.length === 0) return;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedNotifications.length} notification(s) ?`)) return;

    try {
      await Promise.all(
        selectedNotifications.map((id) => notificationService.deleteNotification(id))
      );
      setNotifications((prev) => prev.filter((n) => !selectedNotifications.includes(n.id)));
      setSelectedNotifications([]);
      toast.success(`${selectedNotifications.length} notification(s) supprimée(s)`);
    } catch (error) {
      console.error('Error deleting notifications:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAllRead = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer toutes les notifications lues ?')) return;

    try {
      await notificationService.deleteAllRead(userProfile.id);
      setNotifications((prev) => prev.filter((n) => !n.is_read));
      toast.success('Notifications lues supprimées');
    } catch (error) {
      console.error('Error deleting read notifications:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSelectNotification = (id) => {
    setSelectedNotifications((prev) =>
      prev.includes(id) ? prev.filter((nId) => nId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map((n) => n.id));
    }
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'urgent':
        return { name: 'AlertCircle', color: 'text-red-500' };
      case 'warning':
        return { name: 'AlertTriangle', color: 'text-orange-500' };
      case 'success':
        return { name: 'CheckCircle', color: 'text-green-500' };
      case 'error':
        return { name: 'XCircle', color: 'text-red-600' };
      default:
        return { name: 'Info', color: 'text-blue-500' };
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    return past.toLocaleDateString('fr-FR');
  };

  const getTypeLabel = (type) => {
    const labels = {
      info: 'Information',
      warning: 'Avertissement',
      urgent: 'Urgent',
      success: 'Succès',
      error: 'Erreur',
    };
    return labels[type] || type;
  };

  const stats = {
    total: notifications.length,
    unread: notifications.filter((n) => !n.is_read).length,
    read: notifications.filter((n) => n.is_read).length,
    urgent: notifications.filter((n) => n.type === 'urgent' && !n.is_read).length,
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Notifications</h1>
              <p className="text-muted-foreground mt-2">
                Gérez toutes vos notifications en un seul endroit
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                </div>
                <Icon name="Bell" size={32} className="text-muted-foreground" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Non lues</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.unread}</p>
                </div>
                <Icon name="BellRing" size={32} className="text-blue-600" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Lues</p>
                  <p className="text-2xl font-bold text-green-600">{stats.read}</p>
                </div>
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Urgentes</p>
                  <p className="text-2xl font-bold text-red-600">{stats.urgent}</p>
                </div>
                <Icon name="AlertCircle" size={32} className="text-red-600" />
              </div>
            </div>
          </div>

          {/* Filters and Actions */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Read Status Filter */}
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Toutes</option>
                  <option value="unread">Non lues</option>
                  <option value="read">Lues</option>
                </select>

                {/* Type Filter */}
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Tous les types</option>
                  <option value="info">Information</option>
                  <option value="warning">Avertissement</option>
                  <option value="urgent">Urgent</option>
                  <option value="success">Succès</option>
                  <option value="error">Erreur</option>
                </select>

                {selectedNotifications.length > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {selectedNotifications.length} sélectionnée(s)
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {stats.unread > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                  >
                    <Icon name="CheckCheck" size={16} />
                    <span className="text-sm">Tout marquer comme lu</span>
                  </button>
                )}
                {selectedNotifications.length > 0 && (
                  <button
                    onClick={handleDeleteSelected}
                    className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors flex items-center gap-2"
                  >
                    <Icon name="Trash2" size={16} />
                    <span className="text-sm">Supprimer sélection</span>
                  </button>
                )}
                {stats.read > 0 && (
                  <button
                    onClick={handleDeleteAllRead}
                    className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center gap-2"
                  >
                    <Icon name="Trash" size={16} />
                    <span className="text-sm">Supprimer lues</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="bg-card border border-border rounded-lg">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Chargement des notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-12 text-center">
                <Icon name="Bell" size={64} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">Aucune notification</p>
                <p className="text-sm text-muted-foreground">
                  {filter === 'unread'
                    ? 'Vous n\'avez aucune notification non lue'
                    : filter === 'read'
                    ? 'Vous n\'avez aucune notification lue'
                    : 'Vous n\'avez aucune notification pour le moment'}
                </p>
              </div>
            ) : (
              <>
                {/* Select All Header */}
                <div className="p-4 border-b border-border flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={
                      filteredNotifications.length > 0 &&
                      selectedNotifications.length === filteredNotifications.length
                    }
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-muted-foreground">
                    {filteredNotifications.length} notification(s)
                  </span>
                </div>

                {/* Notifications */}
                <div className="divide-y divide-border">
                  {filteredNotifications.map((notification) => {
                    const icon = getNotificationIcon(notification.type);
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-muted transition-colors ${
                          !notification.is_read ? 'bg-primary/5' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedNotifications.includes(notification.id)}
                            onChange={() => toggleSelectNotification(notification.id)}
                            className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary"
                            onClick={(e) => e.stopPropagation()}
                          />

                          {/* Icon */}
                          <div
                            className={`p-2 rounded-full ${
                              notification.type === 'urgent'
                                ? 'bg-red-100'
                                : notification.type === 'warning'
                                ? 'bg-orange-100'
                                : notification.type === 'success'
                                ? 'bg-green-100'
                                : notification.type === 'error'
                                ? 'bg-red-100'
                                : 'bg-blue-100'
                            }`}
                          >
                            <Icon name={icon.name} size={20} className={icon.color} />
                          </div>

                          {/* Content */}
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h3
                                className={`text-sm font-medium text-foreground ${
                                  !notification.is_read ? 'font-semibold' : ''
                                }`}
                              >
                                {notification.title}
                              </h3>
                              <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground whitespace-nowrap">
                                {getTypeLabel(notification.type)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Icon name="Clock" size={12} />
                                {getTimeAgo(notification.created_at)}
                              </span>
                              {notification.link && (
                                <span className="flex items-center gap-1 text-primary">
                                  <Icon name="ExternalLink" size={12} />
                                  Voir détails
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {!notification.is_read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRead(notification.id);
                                }}
                                className="p-2 hover:bg-muted rounded transition-colors"
                                title="Marquer comme lu"
                              >
                                <Icon name="Check" size={16} className="text-muted-foreground" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(notification.id);
                              }}
                              className="p-2 hover:bg-destructive/10 rounded transition-colors"
                              title="Supprimer"
                            >
                              <Icon name="Trash2" size={16} className="text-muted-foreground" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;